import {
  ArrowsCounterClockwiseIcon,
  ListIcon,
  MetronomeIcon,
  PlayIcon,
  SlidersHorizontalIcon,
  StopIcon,
} from '@phosphor-icons/react'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { dspscript } from './dsp-script.ts'
import { Logo } from './Logo.tsx'
import { OversamplingControl } from './OversamplingControl.tsx'
import {
  useCompileModeStore,
  useGlobalBpmStore,
  useLatencyHintStore,
  useNavBarHeightStore,
  usePlaybackStore,
  useUserProfileMenuStore,
} from './store.ts'
import { UserProfile } from './UserProfile.tsx'
import { VolumeControl } from './VolumeControl.tsx'

interface NavBarProps {
  rightSideButtons?: React.ReactNode
  showPlayStop?: boolean
  onPlay?: () => Promise<void>
  onStop?: () => Promise<void>
}

const NavBarComponent = function NavBar({
  rightSideButtons,
  showPlayStop = true,
  onPlay,
  onStop,
}: NavBarProps) {
  const { isPlaying, setIsPlaying } = usePlaybackStore()
  const { globalBpm, setGlobalBpm } = useGlobalBpmStore()
  const { latencyHint, setLatencyHint } = useLatencyHintStore()
  const { setHeight } = useNavBarHeightStore()
  const { compileMode, setCompileMode } = useCompileModeStore()
  const [syncBars, setSyncBars] = useState<number>(0)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isMediumMenuOpen, setIsMediumMenuOpen] = useState(false)
  const [isSyncMenuOpen, setIsSyncMenuOpen] = useState(false)
  const [isAudioMenuOpen, setIsAudioMenuOpen] = useState(false)
  const [isSmallScreen, setIsSmallScreen] = useState(false)
  const [isMediumScreen, setIsMediumScreen] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const mediumMenuRef = useRef<HTMLDivElement>(null)
  const syncMenuRef = useRef<HTMLDivElement>(null)
  const audioMenuRef = useRef<HTMLDivElement>(null)
  const navRef = useRef<HTMLElement>(null)
  const { addCloseCallback, removeCloseCallback } = useUserProfileMenuStore()

  useEffect(() => {
    const closeCallback = () => {
      setIsMenuOpen(false)
      setIsMediumMenuOpen(false)
      setIsSyncMenuOpen(false)
      setIsAudioMenuOpen(false)
    }
    addCloseCallback(closeCallback)
    return () => {
      removeCloseCallback(closeCallback)
    }
  }, [addCloseCallback, removeCloseCallback])

  useEffect(() => {
    dspscript.setSyncMode(syncBars)
  }, [syncBars])

  useEffect(() => {
    const interval = setInterval(() => {
      const actualPlaying = dspscript.isPlaying()
      if (actualPlaying !== isPlaying) {
        setIsPlaying(actualPlaying)
      }
    }, 500)

    return () => clearInterval(interval)
  }, [isPlaying, setIsPlaying])

  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth < 1200)
      setIsMediumScreen(window.innerWidth < 1450)
      setIsCollapsed(window.innerWidth < 700)
    }

    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)
    return () => window.removeEventListener('resize', checkScreenSize)
  }, [])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsMenuOpen(false)
      }
      if (mediumMenuRef.current && !mediumMenuRef.current.contains(e.target as Node)) {
        setIsMediumMenuOpen(false)
      }
      if (syncMenuRef.current && !syncMenuRef.current.contains(e.target as Node)) {
        setIsSyncMenuOpen(false)
      }
      if (audioMenuRef.current && !audioMenuRef.current.contains(e.target as Node)) {
        setIsAudioMenuOpen(false)
      }
    }

    if (isMenuOpen || isMediumMenuOpen || isSyncMenuOpen || isAudioMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isMenuOpen, isMediumMenuOpen, isSyncMenuOpen, isAudioMenuOpen])

  useEffect(() => {
    if (!navRef.current) return

    const updateHeight = () => {
      if (navRef.current) {
        setHeight(navRef.current.offsetHeight)
      }
    }

    updateHeight()

    const resizeObserver = new ResizeObserver(updateHeight)
    resizeObserver.observe(navRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [setHeight, isCollapsed, isMenuOpen, isMediumMenuOpen, isSyncMenuOpen, isAudioMenuOpen])

  const handlePlay = useCallback(async () => {
    try {
      if (onPlay) {
        await onPlay()
      }
      else {
        if (!dspscript.getAudioContext()) {
          await dspscript.init(latencyHint)
        }
        if (!dspscript.isPlaying()) {
          await dspscript.start()

          // Poll until audio actually starts playing
          const maxAttempts = 50
          let attempts = 0
          const checkPlaying = () => {
            if (dspscript.isPlaying()) {
              setIsPlaying(true)
            }
            else if (attempts < maxAttempts) {
              attempts++
              setTimeout(checkPlaying, 20)
            }
          }
          checkPlaying()
        }
      }
    }
    catch (err: any) {
      console.error('Failed to start playback:', err)
    }
  }, [onPlay, latencyHint, setIsPlaying])

  const handleStop = useCallback(async () => {
    try {
      if (onStop) {
        await onStop()
      }
      else {
        await dspscript.stop()
        setIsPlaying(false)
      }
    }
    catch (err: any) {
      console.error('Failed to stop playback:', err)
    }
  }, [onStop, setIsPlaying])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!e.ctrlKey) return

      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault()
        setSyncBars(parseInt(e.key))
      }
      else if (e.key === '[') {
        e.preventDefault()
        setCompileMode('key')
      }
      else if (e.key === ']') {
        e.preventDefault()
        setCompileMode('ctrl+enter')
      }
      else if (e.key === ' ') {
        e.preventDefault()
        if (isPlaying) {
          handleStop()
        }
        else {
          handlePlay()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isPlaying, handlePlay, handleStop])

  const handleBpmChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value)
    if (!isNaN(value) && value > 0) {
      setGlobalBpm(value)
      dspscript.setGlobalBpm(value)
    }
  }

  const handleLatencyHintChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(event.target.value)
    if (!isNaN(value) && value >= 0) {
      setLatencyHint(value)
    }
  }

  const handleSyncBarsChange = (bars: number) => {
    setSyncBars(bars)
  }

  const handleCompileModeToggle = () => {
    setCompileMode(compileMode === 'key' ? 'ctrl+enter' : 'key')
  }

  const ctrlEnter = (
    <div className="flex gap-1">
      <span className="text-neutral-400">Ctrl</span>
      <span className="">Enter</span>
    </div>
  )

  const menuContent = (
    <>
      <div className="px-4 py-2 border-b border-[#444] relative -right-2">
        <VolumeControl />
      </div>
      <div className="px-4 py-2 border-b border-[#444]">
        <div className="flex gap-2 items-center">
          <span className="text-[#888] w-16 text-right">BPM:</span>
          <input
            type="number"
            value={globalBpm}
            onChange={handleBpmChange}
            className="w-[70px] px-2 py-1 bg-gradient-to-br from-neutral-900 to-neutral-700 text-sm font-semibold border border-none rounded-md"
            min="1"
            max="666"
          />
        </div>
      </div>
      <button
        title='Compile on Edit "Ctrl+[" or Ctrl+Enter "Ctrl+]"'
        className="w-full px-4 py-2 text-left hover:bg-[#333] cursor-pointer flex items-center gap-2 border-b border-[#444]"
        onClick={handleCompileModeToggle}
      >
        <ArrowsCounterClockwiseIcon weight="regular" size={16} />
        {compileMode === 'key' ? 'Edit' : ctrlEnter}
      </button>
      <div className="px-4 py-2 border-b border-[#444]">
        <div className="flex gap-2 items-center mb-2">
          <MetronomeIcon weight="regular" size={16} />
          <span className="text-[#888]">Sync:</span>
        </div>
        <div className="text-right">
          {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(bars => (
            <button
              key={bars}
              title={`Sync: ${bars === 0 ? 'Now' : `${bars} bar${bars > 1 ? 's' : ''}`}`}
              className={`px-2 py-1 text-xs border-none rounded cursor-pointer font-semibold ${
                syncBars === bars
                  ? 'bg-gradient-to-br from-orange-400 to-red-600 text-white'
                  : 'bg-gradient-to-br from-neutral-700 to-neutral-900 text-white hover:opacity-90'
              }`}
              onClick={() => handleSyncBarsChange(bars)}
            >
              {bars === 0 ? 'Now' : bars}
            </button>
          ))}
        </div>
      </div>
      <div className="px-4 py-2 border-b border-[#444]">
        <div className="flex gap-2 items-center">
          <span className="text-[#888] w-16 text-right">Latency:</span>
          <input
            type="number"
            value={latencyHint}
            onChange={handleLatencyHintChange}
            className="w-[90px] px-2 py-1 bg-gradient-to-br from-neutral-900 to-neutral-700 text-sm font-semibold border border-none rounded-md"
            min="0"
            max="1"
            step="0.0125"
          />
        </div>
      </div>
      <div className="px-4 py-2">
        <OversamplingControl />
      </div>
    </>
  )

  const audioMenuContent = (
    <>
      <div className="px-4 py-2 border-b border-[#444]">
        <div className="flex gap-2 items-center">
          <span className="text-[#888] w-16 text-right">Latency:</span>
          <input
            type="number"
            value={latencyHint}
            onChange={handleLatencyHintChange}
            className="w-[90px] px-2 py-1 bg-gradient-to-br from-neutral-900 to-neutral-700 text-sm font-semibold border border-none rounded-md"
            min="0"
            max="1"
            step="0.0125"
          />
        </div>
      </div>
      <div className="px-4 py-2">
        <OversamplingControl />
      </div>
    </>
  )

  const mediumMenuContent = (
    <>
      <button
        title='Compile on Edit "Ctrl+[" or Ctrl+Enter "Ctrl+]"'
        className="w-full px-4 py-2 text-left hover:bg-[#333] cursor-pointer flex items-center gap-2 border-b border-[#444]"
        onClick={handleCompileModeToggle}
      >
        <ArrowsCounterClockwiseIcon weight="regular" size={16} />
        {compileMode === 'key' ? 'Edit' : ctrlEnter}
      </button>
      <div className="px-4 py-2 border-b border-[#444]">
        <div className="flex gap-2 items-center mb-2">
          <MetronomeIcon weight="regular" size={16} />
          <span className="text-[#888]">Sync:</span>
        </div>
        <div className="text-right">
          {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(bars => (
            <button
              key={bars}
              title={`Sync: ${bars === 0 ? 'Now' : `${bars} bar${bars > 1 ? 's' : ''}`} "Ctrl+0..9"`}
              className={`px-2 py-1 text-xs border-none rounded cursor-pointer font-semibold ${
                syncBars === bars
                  ? 'bg-gradient-to-br from-orange-400 to-red-600 text-white'
                  : 'bg-gradient-to-br from-neutral-700 to-neutral-900 text-white hover:opacity-90'
              }`}
              onClick={() => handleSyncBarsChange(bars)}
            >
              {bars === 0 ? 'Now' : bars}
            </button>
          ))}
        </div>
      </div>
      {audioMenuContent}
    </>
  )

  const syncMenuContent = (
    <div className="px-4 py-2">
      <div className="flex gap-2 items-center mb-2">
        <MetronomeIcon weight="regular" size={16} />
        <span className="text-[#888]">Sync:</span>
      </div>
      <div className="text-right">
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(bars => (
          <button
            key={bars}
            title={`Sync: ${bars === 0 ? 'Now' : `${bars} bar${bars > 1 ? 's' : ''}`} "Ctrl+0..9"`}
            className={`px-2 py-1 text-xs border-none rounded cursor-pointer font-semibold ${
              syncBars === bars
                ? 'bg-gradient-to-br from-orange-400 to-red-600 text-white'
                : 'bg-gradient-to-br from-neutral-700 to-neutral-900 text-white hover:opacity-90'
            }`}
            onClick={() => handleSyncBarsChange(bars)}
          >
            {bars === 0 ? 'Now' : bars}
          </button>
        ))}
      </div>
    </div>
  )

  return (
    <nav
      ref={navRef}
      className={`${
        isCollapsed ? 'h-auto min-h-[60px] py-2' : 'h-[60px]'
      } bg-black px-5 border-b-2 border-orange-500 flex ${
        isCollapsed ? 'flex-col' : 'items-center justify-between'
      } shrink-0`}
    >
      {isCollapsed
        ? (
          <div className="flex items-center -my-2 -mb-3 w-full">
            <Logo />
          </div>
        )
        : (
          <>
            <div className="flex items-center gap-5">
              <Logo />
              {!isSmallScreen && <VolumeControl />}
              <div className="flex gap-2 text-sm items-center">
                {showPlayStop && (
                  <>
                    <div className="flex items-center gap-2">
                      <button
                        title={`Play "Ctrl+Space"`}
                        className="px-4 h-8 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-orange-400 to-red-600 text-white hover:opacity-90 flex items-center gap-2"
                        onPointerDown={handlePlay}
                      >
                        <PlayIcon weight="fill" size={16} />
                      </button>
                      <button
                        title={`Stop "Ctrl+Space"`}
                        className="px-4 h-8 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-neutral-700 to-neutral-900 text-white hover:bg-[#444] flex items-center gap-2"
                        onPointerDown={handleStop}
                      >
                        <StopIcon weight="fill" size={16} />
                      </button>
                    </div>
                  </>
                )}
                {!isSmallScreen && (
                  <>
                    <div className="flex gap-2 items-center">
                      <span className="text-[#888] select-none">BPM:</span>
                      <input
                        type="number"
                        value={globalBpm}
                        onChange={handleBpmChange}
                        className="w-[60px] px-2 py-1 bg-gradient-to-br from-neutral-900 to-neutral-700 text-sm font-semibold border border-none rounded-md"
                        min="1"
                        max="666"
                      />
                    </div>
                    {!isMediumScreen && (
                      <>
                        <div
                          className="relative"
                          ref={syncMenuRef}
                        >
                          <button
                            title={`Sync: ${
                              syncBars === 0 ? 'Now' : `${syncBars} bar${syncBars > 1 ? 's' : ''}`
                            } "Ctrl+0..9"`}
                            className="px-4 h-8 text-sm border-none cursor-pointer font-semibold bg-gradient-to-br from-neutral-700 to-neutral-900 text-white hover:opacity-90 flex items-center gap-2 rounded-md"
                            onClick={() => setIsSyncMenuOpen(!isSyncMenuOpen)}
                          >
                            <MetronomeIcon weight="regular" size={16} />
                            {syncBars === 0 ? 'Now' : `${syncBars} bar${syncBars > 1 ? 's' : ''}`}
                          </button>
                          {isSyncMenuOpen && (
                            <div className="z-50 absolute left-0 top-12 bg-[#2a2a2a] border border-[#444] rounded-lg shadow-lg min-w-[200px] overflow-hidden">
                              {syncMenuContent}
                            </div>
                          )}
                        </div>
                        <button
                          title='Compile on Edit "Ctrl+[" or Ctrl+Enter "Ctrl+]"'
                          className="px-4 h-8 text-sm border-none cursor-pointer font-semibold bg-gradient-to-br from-neutral-700 to-neutral-900 text-white hover:opacity-90 flex items-center gap-2 rounded-md"
                          onClick={handleCompileModeToggle}
                        >
                          <ArrowsCounterClockwiseIcon weight="regular" size={16} />
                          {compileMode === 'key' ? 'Edit' : ctrlEnter}
                        </button>
                      </>
                    )}
                    {isMediumScreen && (
                      <div className="relative" ref={mediumMenuRef}>
                        <button
                          onClick={() => setIsMediumMenuOpen(!isMediumMenuOpen)}
                          className="px-4 h-8 text-sm border-none cursor-pointer font-semibold bg-gradient-to-br from-neutral-700 to-neutral-900 text-white hover:opacity-90 flex items-center gap-2 rounded-md"
                        >
                          <ListIcon weight="regular" size={16} />
                        </button>
                        {isMediumMenuOpen && (
                          <div className="z-50 absolute left-0 top-12 bg-[#2a2a2a] border border-[#444] rounded-lg shadow-lg min-w-[200px] overflow-hidden">
                            {mediumMenuContent}
                          </div>
                        )}
                      </div>
                    )}
                    {!isMediumScreen && (
                      <div
                        className="relative"
                        ref={audioMenuRef}
                      >
                        <button
                          title="Audio Settings"
                          className="px-4 h-8 text-sm border-none cursor-pointer font-semibold bg-gradient-to-br from-neutral-700 to-neutral-900 text-white hover:opacity-90 flex items-center gap-2 rounded-md"
                          onClick={() => setIsAudioMenuOpen(!isAudioMenuOpen)}
                        >
                          <ListIcon weight="regular" size={16} />
                        </button>
                        {isAudioMenuOpen && (
                          <div className="z-50 absolute left-0 top-12 bg-[#2a2a2a] border border-[#444] rounded-lg shadow-lg min-w-[200px] overflow-hidden">
                            {audioMenuContent}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
                {isSmallScreen && (
                  <div className="relative" ref={menuRef}>
                    <button
                      onClick={() => setIsMenuOpen(!isMenuOpen)}
                      className="px-4 h-8 text-sm border-none cursor-pointer font-semibold bg-gradient-to-br from-neutral-700 to-neutral-900 text-white hover:opacity-90 flex items-center gap-2 rounded-md"
                    >
                      <ListIcon weight="regular" size={16} />
                    </button>
                    {isMenuOpen && (
                      <div className="z-50 absolute left-0 top-12 bg-[#2a2a2a] border border-[#444] rounded-lg shadow-lg min-w-[200px] overflow-hidden">
                        {menuContent}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2 text-sm items-center whitespace-nowrap">
              {rightSideButtons}
              <UserProfile />
            </div>
          </>
        )}
      {isCollapsed && (
        <div className="flex gap-2 text-sm items-center w-full mt-2 flex-wrap">
          {showPlayStop && (
            <>
              <div className="flex items-center gap-2">
                <button
                  title={`Play "Ctrl+Space"`}
                  className="px-4 h-8 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-orange-400 to-red-600 text-white hover:opacity-90 flex items-center gap-2"
                  onPointerDown={handlePlay}
                >
                  <PlayIcon weight="fill" size={16} />
                </button>
                <button
                  title={`Stop "Ctrl+Space"`}
                  className="px-4 h-8 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-neutral-700 to-neutral-900 text-white hover:bg-[#444] flex items-center gap-2"
                  onPointerDown={handleStop}
                >
                  <StopIcon weight="fill" size={16} />
                </button>
              </div>
            </>
          )}
          {isSmallScreen && (
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="px-4 h-8 text-sm border-none cursor-pointer font-semibold bg-gradient-to-br from-neutral-700 to-neutral-900 text-white hover:opacity-90 flex items-center gap-2 rounded-md"
              >
                <ListIcon weight="regular" size={16} />
              </button>
              {isMenuOpen && (
                <div className="z-50 absolute left-0 top-12 bg-[#2a2a2a] border border-[#444] rounded-lg shadow-lg min-w-[200px] overflow-hidden">
                  {menuContent}
                </div>
              )}
            </div>
          )}
          {!isSmallScreen && (
            <>
              <VolumeControl />
              <div className="flex gap-2 items-center">
                <span className="text-[#888]">BPM:</span>
                <input
                  type="number"
                  value={globalBpm}
                  onChange={handleBpmChange}
                  className="w-[70px] px-2 py-1 bg-gradient-to-br from-neutral-900 to-neutral-700 text-sm font-semibold border border-none rounded-md"
                  min="1"
                  max="666"
                />
              </div>
              {!isMediumScreen && (
                <>
                  <div
                    className="relative"
                    ref={syncMenuRef}
                  >
                    <button
                      title={`Sync: ${
                        syncBars === 0 ? 'Now' : `${syncBars} bar${syncBars > 1 ? 's' : ''}`
                      } "Ctrl+0..9"`}
                      className="px-4 h-8 text-sm border-none cursor-pointer font-semibold bg-gradient-to-br from-neutral-700 to-neutral-900 text-white hover:opacity-90 flex items-center gap-2 rounded-md"
                      onClick={() => setIsSyncMenuOpen(!isSyncMenuOpen)}
                    >
                      <MetronomeIcon weight="regular" size={16} />
                      {syncBars === 0 ? 'Now' : `${syncBars} bar${syncBars > 1 ? 's' : ''}`}
                    </button>
                    {isSyncMenuOpen && (
                      <div className="z-50 absolute left-0 top-12 bg-[#2a2a2a] border border-[#444] rounded-lg shadow-lg min-w-[200px] overflow-hidden">
                        {syncMenuContent}
                      </div>
                    )}
                  </div>
                  <button
                    title='Compile on Edit "Ctrl+[" or Ctrl+Enter "Ctrl+]"'
                    className="px-4 h-8 text-sm border-none cursor-pointer font-semibold bg-gradient-to-br from-neutral-700 to-neutral-900 text-white hover:opacity-90 flex items-center gap-2 rounded-md"
                    onClick={handleCompileModeToggle}
                  >
                    <ArrowsCounterClockwiseIcon weight="regular" size={16} />
                    {compileMode === 'key' ? 'Edit' : ctrlEnter}
                  </button>
                </>
              )}
              {isMediumScreen && (
                <div className="relative" ref={mediumMenuRef}>
                  <button
                    onClick={() => setIsMediumMenuOpen(!isMediumMenuOpen)}
                    className="px-4 h-8 text-sm border-none cursor-pointer font-semibold bg-gradient-to-br from-neutral-700 to-neutral-900 text-white hover:opacity-90 flex items-center gap-2 rounded-md"
                  >
                    <ListIcon weight="regular" size={16} />
                  </button>
                  {isMediumMenuOpen && (
                    <div className="z-50 absolute left-0 top-12 bg-[#2a2a2a] border border-[#444] rounded-lg shadow-lg min-w-[200px] overflow-hidden">
                      {mediumMenuContent}
                    </div>
                  )}
                </div>
              )}
              {!isMediumScreen && (
                <div
                  className="relative"
                  ref={audioMenuRef}
                >
                  <button
                    title="Audio Settings"
                    className="px-4 h-8 text-sm border-none cursor-pointer font-semibold bg-gradient-to-br from-neutral-700 to-neutral-900 text-white hover:opacity-90 flex items-center gap-2 rounded-md"
                    onClick={() => setIsAudioMenuOpen(!isAudioMenuOpen)}
                  >
                    <SlidersHorizontalIcon weight="regular" size={16} />
                  </button>
                  {isAudioMenuOpen && (
                    <div className="z-50 absolute left-0 top-12 bg-[#2a2a2a] border border-[#444] rounded-lg shadow-lg min-w-[200px] overflow-hidden">
                      {audioMenuContent}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          <div className="flex gap-2 text-sm items-center ml-auto">
            {rightSideButtons}
            <UserProfile />
          </div>
        </div>
      )}
    </nav>
  )
}

// Wrap in memo to prevent reconciliation when parent re-renders
export const NavBar = memo(NavBarComponent)
